import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, DropdownMenu, Icon } from "semantic-ui-react";

import { Dropdown } from "components";
import "modules/AppointmentDetails/components/AppointmentReportDropdown/AppointmentReportDropdown.scss";
import { DropdownItem } from "modules/AppointmentDetails/components/AppointmentReportDropdown/components";
import { useDownloadAppointmentReport } from "modules/AppointmentDetails/components/AppointmentReportDropdown/hooks/useDownloadAppointmentReport";
import {
  APPOINTMENT_REPORT_SECTION_TYPE,
  LOG_OPTION_KEYS,
  useDropdownOptions
} from "modules/AppointmentDetails/components/AppointmentReportDropdown/hooks/useDropdownOptions";
import { useRealTimeOrderedChecks } from "modules/AppointmentDetails/hooks";

import type { Appointment } from "models";
import type { FormEvent } from "react";

type AppointmentReportDropdownProps = {
  appointment: Appointment;
};

export const AppointmentReportDropdown = ({ appointment }: AppointmentReportDropdownProps) => {
  const { t } = useTranslation();

  const [selectedChecklistsIds, setSelectedChecklistsIds] = useState<number[]>([]);
  const [selectedLogKeys, setSelectedLogKeys] = useState<string[]>([]);

  const { checks } = useRealTimeOrderedChecks(appointment.id);
  const dropdownOptions = useDropdownOptions(checks, appointment);
  const downloadAppointmentReportMutation = useDownloadAppointmentReport();

  if (!checks?.length) return null;

  const logOptionKeys = dropdownOptions.flatMap(o => (o.sectionType === APPOINTMENT_REPORT_SECTION_TYPE.Logs ? [o.key] : []));

  const isCheckboxChecked = (id: number) => selectedChecklistsIds.includes(id);
  const isLogChecked = (key: string) => selectedLogKeys.includes(key);

  const handleSelectChecks = (event: FormEvent, checklistId: number) => {
    event.stopPropagation();
    setSelectedChecklistsIds(prev => (prev.includes(checklistId) ? prev.filter(id => id !== checklistId) : [...prev, checklistId]));
  };

  const handleSelectCommunicationSignLogs = (event: FormEvent, key: string) => {
    event.stopPropagation();
    setSelectedLogKeys(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));
  };

  const handleChangeSelectAll = (event: FormEvent) => {
    event.stopPropagation();
    const isAllSelected = selectedChecklistsIds.length === checks.length && selectedLogKeys.length === logOptionKeys.length;

    if (isAllSelected) {
      setSelectedChecklistsIds([]);
      setSelectedLogKeys([]);
    } else {
      setSelectedChecklistsIds(checks.map(({ id }) => id));
      setSelectedLogKeys(logOptionKeys);
    }
  };

  const handleDownloadPdf = () =>
    downloadAppointmentReportMutation.mutate({
      appointment_id: appointment.id,
      car_check_ids: selectedChecklistsIds,
      activity_logs: selectedLogKeys.includes(LOG_OPTION_KEYS.ActivityLog),
      customer_sign_logs: selectedLogKeys.includes(LOG_OPTION_KEYS.CustomerSignLogs)
    });

  const isAllSelected = checks.length > 0 && selectedChecklistsIds.length === checks.length && selectedLogKeys.length === logOptionKeys.length;

  return (
    <Dropdown
      className="AppointmentReportDropdown"
      icon={null}
      trigger={
        <Button basic className="appointment-report-dropdown-button">
          <Icon className="file pdf" />
        </Button>
      }
      pointing="top right"
      closeOnChange={false}
      selectOnBlur={false}
    >
      <DropdownMenu className="appointment-report-dropdown-menu">
        <div className="appointment-report-dropdown-item" onClick={e => e.stopPropagation()}>
          <Checkbox checked={isAllSelected} onChange={event => handleChangeSelectAll(event)} label={t("v8_select_all", "Select All")} />
        </div>

        {dropdownOptions.map(option => {
          const isCheck = option.sectionType === APPOINTMENT_REPORT_SECTION_TYPE.Check;
          return (
            <DropdownItem
              key={option.key}
              option={option}
              checked={isCheck ? isCheckboxChecked(option.value) : isLogChecked(option.key)}
              onChange={event => (isCheck ? handleSelectChecks(event, option.value) : handleSelectCommunicationSignLogs(event, option.key))}
            />
          );
        })}

        <div className="appointment-report-dropdown-download-button">
          <Button fluid color="green" disabled={!selectedChecklistsIds.length && !selectedLogKeys.length} onClick={handleDownloadPdf}>
            {t("v8_download", "Download")}
          </Button>
        </div>
      </DropdownMenu>
    </Dropdown>
  );
};
